/**
 * The rules behind the sequence authoring screens.
 *
 * Named for the concern rather than the component, like lifecycleRules.ts
 * beside LifecyclePanel.tsx and touchLog.ts beside TouchHistory.tsx.
 *
 * WHY THE RULES ARE HERE AND NOT IN THE COMPONENT
 * -----------------------------------------------
 * The `marketing` vitest project globs plain `.ts` and runs in Node; `app`
 * runs in real Chromium and costs a ~130 MB Playwright download. Every rule
 * below decides what gets WRITTEN to `sequence_steps`, and two of them
 * (planRemoval's renumbering, and its absorption of the removed wait) are the
 * difference between a cadence that runs and one that silently drops people
 * halfway through while reporting that they finished. None of that needs a
 * DOM, so none of it should sit one browser install away from being skipped.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE TWO TRAPS THIS FILE EXISTS TO CLOSE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * 1. A GAP IN step_no SILENTLY COMPLETES ENROLLMENTS, WITH A FALSE REASON.
 *
 *    public.advance_enrollments() walks steps strictly sequentially:
 *
 *        join public.sequence_steps st
 *          on st.sequence_id = e.sequence_id
 *         and st.step_no = e.current_step + 1
 *
 *    and decides the cadence is over by looking for the step after the
 *    current one:
 *
 *        status       = case when nxt.step_no is null then 'completed' ... end,
 *        ended_reason = case when nxt.step_no is null then 'last_step' end,
 *
 *    So delete step 2 from a sequence numbered 1,2,3 and every enrollment
 *    sitting on step 1 finds no step 2, is marked `completed` with
 *    ended_reason `last_step`, and step 3 never runs. No error anywhere. The
 *    record then CLAIMS the person finished the cadence when they were
 *    dropped halfway -- and an ending nobody can account for is the same
 *    defect as a lead that just went quiet, which is exactly what the
 *    `enrollments_ended_has_reason` constraint was added to prevent. A false
 *    reason defeats that constraint while satisfying it.
 *
 *    Hence planRemoval(): removing a step ALWAYS renumbers the rest to stay
 *    contiguous. And numberingGap() exists because rows can also arrive from
 *    a hand-written SQL statement, so the screen has to be able to say the
 *    damage is already there.
 *
 * 2. offset_days IS RELATIVE TO THE PREVIOUS STEP, SO REMOVING ONE MOVES
 *    EVERYTHING AFTER IT.
 *
 *    052 chose that deliberately -- "days after the PREVIOUS step, not after
 *    enrollment, so inserting a step in the middle does not silently
 *    reschedule everything after it". The consequence for removal is the
 *    mirror image, and it is not benign: deleting step 2 of [0, 3, 4] leaves
 *    [0, 4], and the step that was due on day 7 is now due on day 4. Nobody
 *    asked for that, and nothing would say it happened.
 *
 *    Hence planRemoval() adds the removed step's offset_days to the
 *    following step's, so [0, 3, 4] minus its middle step becomes [0, 7] --
 *    day 0 and day 7, exactly where they were.
 * ══════════════════════════════════════════════════════════════════════════
 */

import type { SequenceStep } from "./types";

/**
 * The two `track` values `sequences_track_check` permits. NULL is the third
 * state and is not in this list, because it is not a value -- see below.
 *
 * `{ id, name }` rather than `{ value, label }`: these are fed straight to
 * react-admin's SelectInput, which reads those keys.
 */
export const SEQUENCE_TRACK_CHOICES = [
  { id: "prospect", name: "Prospect" },
  { id: "partner", name: "Partner" },
];

/**
 * What a NULL track means, in words.
 *
 * NULL IS NOT "UNSET". `check (track is null or track in ('prospect',
 * 'partner'))` gives the column exactly three states and all three are
 * answers: this cadence is for prospects, for partners, or for either. A
 * blank cell on the list would read as the one thing it does not mean --
 * somebody forgot -- so it is labeled everywhere it appears.
 */
export const ANY_TRACK_LABEL = "Any track";

export const trackLabel = (track: string | null | undefined) =>
  track == null
    ? ANY_TRACK_LABEL
    : (SEQUENCE_TRACK_CHOICES.find((c) => c.id === track)?.name ?? track);

/**
 * The empty selection, turned back into NULL before it is written.
 *
 * REQUIRED, not tidiness. react-admin's SelectInput represents "nothing
 * chosen" as the empty string and refuses to represent it as null -- its own
 * source says so: "emptyValue being set to null or undefined is not
 * supported. Use parse to turn the empty string into null." Saving that empty
 * string straight through would send `track: ""` to Postgres, where
 * `sequences_track_check` rejects it outright and the author gets a
 * constraint error for the perfectly ordinary act of not picking a track.
 */
export const emptyTrackToNull = (value: unknown) =>
  value === "" || value == null ? null : value;

/**
 * The channels a step may use.
 *
 * `sequence_steps.channel` HAS NO CHECK CONSTRAINT -- it is `text not null
 * default 'email'` and nothing more, so this list is the only guard there is.
 * That is the same shape of exposure as task_type below, with one mercy:
 * nothing reads `channel` yet, so a typo here is cosmetic today rather than
 * silently wrong. It will not stay that way, which is why the vocabulary is
 * fixed now rather than after something depends on it.
 *
 * The ids mirror `content_assets.kind` (051) so that a step's channel and the
 * kind of template it points at are the same words and can be compared at a
 * glance. The single exception is `call`, where the asset kind is
 * `call_script`: the asset is a script, the channel is a phone call, and
 * naming the channel after the document would be naming the wrong noun.
 */
export const SEQUENCE_CHANNELS: { value: string; label: string }[] = [
  { value: "email", label: "Email" },
  { value: "call", label: "Call" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "note", label: "Note" },
  { value: "other", label: "Other" },
];

export const channelLabel = (channel: string) =>
  SEQUENCE_CHANNELS.find((c) => c.value === channel)?.label ?? channel;

/**
 * The step number a newly added step should take.
 *
 * max + 1, NOT length + 1. They differ exactly when the numbering already has
 * a gap -- steps 1 and 3, say -- where length + 1 is 3 and collides with the
 * UNIQUE (sequence_id, step_no) index, so the insert fails with a constraint
 * error on a screen that has done nothing wrong. max + 1 appends cleanly and
 * leaves the pre-existing gap for numberingGap() to report, which is the
 * right division of labor: adding a step is not the moment to silently
 * rewrite numbers somebody else's enrollments are sitting on.
 */
export function nextStepNo(steps: readonly Pick<SequenceStep, "step_no">[]) {
  return steps.reduce((max, s) => Math.max(max, s.step_no), 0) + 1;
}

/** Ascending by step_no. Never mutates the caller's array. */
const inOrder = <T extends Pick<SequenceStep, "step_no">>(
  steps: readonly T[],
) => [...steps].sort((a, b) => a.step_no - b.step_no);

export type ScheduledStep<T> = T & {
  /**
   * Days from enrollment, cumulative. DISPLAY ONLY -- nothing is written from
   * this, and the database keeps only the relative offsets.
   */
  day: number;
};

/**
 * Turn relative `offset_days` into cumulative days from enrollment.
 *
 * WHY THIS IS NOT A ONE-LINER WORTH INLINING. The stored number answers "how
 * long after the last step?" and the only question an author actually has is
 * "when does this land?". Reading a column of 0, 3, 4 as days 0, 3 and 4 is
 * the natural mistake, it is wrong by three days on the third step, and the
 * error compounds with every step added. Showing both numbers, with the
 * cumulative one derived here rather than eyeballed, is the whole point.
 *
 * The generator adds +/-1 day of jitter per step, so a real task can land a
 * day either side of these numbers -- deliberately, so a weekly sequence does
 * not land on the same weekday for ever and read as automation. The schedule
 * shown here is the intent, not a promise, and the UI says so.
 */
export function stepSchedule<
  T extends Pick<SequenceStep, "step_no" | "offset_days">,
>(steps: readonly T[]): ScheduledStep<T>[] {
  let day = 0;
  return inOrder(steps).map((step) => {
    day += step.offset_days;
    return { ...step, day };
  });
}

/**
 * The first step number missing from an otherwise-numbered sequence, or null.
 *
 * This is trap 1 made visible. The screens here can no longer CREATE a gap --
 * planRemoval() renumbers -- but `sequence_steps` is an ordinary table with an
 * ordinary RLS policy, and one hand-run DELETE in the SQL editor is all it
 * takes. A sequence in that state does not misbehave loudly; it quietly ends
 * every enrollment that reaches the hole and files the ending as `last_step`.
 * So the detail screen asks this question on every render and says so in red.
 *
 * Returns the first absent number, counting from 1, because that is the step
 * at which enrollments stop -- everything after it is unreachable, not merely
 * misnumbered.
 */
export function numberingGap(
  steps: readonly Pick<SequenceStep, "step_no">[],
): number | null {
  const ordered = inOrder(steps);
  for (let i = 0; i < ordered.length; i++) {
    if (ordered[i].step_no !== i + 1) return i + 1;
  }
  return null;
}

export type RemovalPlan = {
  /** The row to delete. */
  remove: SequenceStep;
  /**
   * The rows to update afterwards, ALREADY IN THE ORDER THEY MUST BE WRITTEN.
   * See the ordering note on planRemoval().
   */
  renumber: { id: number; step_no: number; offset_days: number }[];
  /**
   * The step that inherits the removed step's wait, if there is one. Null
   * when the last step is removed -- there is nothing after it to move.
   */
  absorbedBy: SequenceStep | null;
  /** How many days were moved onto it. Zero is a normal answer. */
  absorbedDays: number;
  /** The list as it will be once every write lands. What the UI previews. */
  after: SequenceStep[];
};

/**
 * Everything that has to happen to remove one step, worked out before
 * anything is written.
 *
 * Returns null if the id is not in the list, so a stale click after somebody
 * else removed the same step is a no-op rather than a delete of whatever now
 * sits at that position.
 *
 * WHY THE RENUMBER ORDER IS PART OF THE CONTRACT
 * ----------------------------------------------
 * `UNIQUE (sequence_id, step_no)` is checked per statement, and these are
 * separate PostgREST requests. Every renumbered row moves DOWN by one, so
 * writing them in ASCENDING order means each write targets a number the row
 * below it has already vacated. Descending order would try to write step 3's
 * new number 2 while step 2 still holds it, and the update fails on a unique
 * violation halfway through -- leaving the sequence in exactly the gapped
 * state this function exists to prevent, with no transaction to roll it back.
 *
 * The delete goes first for the same reason: it frees the removed number.
 */
export function planRemoval(
  steps: readonly SequenceStep[],
  stepId: number,
): RemovalPlan | null {
  const ordered = inOrder(steps);
  const index = ordered.findIndex((s) => s.id === stepId);
  if (index === -1) return null;

  const remove = ordered[index];
  const rest = ordered.filter((_, i) => i !== index);

  /*
   * Trap 2. The removed step's wait belongs to the gap between its
   * predecessor and its successor, not to the step being deleted, so it
   * moves onto the successor and every later step keeps the day it was on.
   * Dropping it instead would pull the whole tail of the sequence forward by
   * that many days, which nobody asked for and nothing would report.
   */
  const absorbedBy = index < ordered.length - 1 ? ordered[index + 1] : null;

  const after = rest.map((step, i) => ({
    ...step,
    // Contiguous from 1 by construction: the position IS the step number.
    step_no: i + 1,
    offset_days:
      step.id === absorbedBy?.id
        ? step.offset_days + remove.offset_days
        : step.offset_days,
  }));

  return {
    remove,
    // Only the rows that actually move. Steps before the removed one keep
    // both their number and their wait, and re-writing them would be a
    // pointless request that could fail on its own.
    renumber: after
      .slice(index)
      .map(({ id, step_no, offset_days }) => ({ id, step_no, offset_days })),
    absorbedBy,
    absorbedDays: absorbedBy ? remove.offset_days : 0,
    after,
  };
}
